/**
 * CompanyPeopleTab Component
 *
 * Displays contacts at a company with role editing.
 * Shows table with name, title, department, email, status, and actions.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contactsApi } from '@/services/contactsApi';
import type { CompanyContact, ContactRoleUpdateInput } from '../types';
import { getInitials } from '../utils';

interface CompanyPeopleTabProps {
  contacts: CompanyContact[];
  companyId: string;
  onUpdate: () => void;
  onError: (message: string) => void;
  onNavigateToContact?: (contactId: string) => void;
}

export default function CompanyPeopleTab({
  contacts,
  companyId: _companyId,
  onUpdate,
  onError,
  onNavigateToContact,
}: CompanyPeopleTabProps) {
  const { t } = useTranslation('people');
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [editRoleForm, setEditRoleForm] = useState<ContactRoleUpdateInput>({});

  const startEditingRole = (contact: CompanyContact) => {
    setEditingRoleId(contact.role_id);
    setEditRoleForm({
      title: contact.title || '',
      department: contact.department || '',
      is_current: contact.is_current,
      is_primary: contact.is_primary,
    });
  };

  const cancelEditingRole = () => {
    setEditingRoleId(null);
    setEditRoleForm({});
  };

  const handleRoleUpdate = async (roleId: string) => {
    try {
      await contactsApi.updateRole(roleId, editRoleForm);
      setEditingRoleId(null);
      setEditRoleForm({});
      onUpdate();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to update role');
    }
  };

  const handleRoleDelete = async (roleId: string, contactName: string) => {
    if (!confirm(t('company_people_tab.remove_confirmation', { name: contactName }))) return;
    try {
      await contactsApi.deleteRoleById(roleId);
      onUpdate();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to remove contact');
    }
  };

  if (contacts.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        <p className="font-medium">{t('company_people_tab.empty_state.title')}</p>
        <p className="text-sm mt-1">{t('company_people_tab.empty_state.description')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('company_people_tab.table.name')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('company_people_tab.table.title')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('company_people_tab.table.department')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('company_people_tab.table.email')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('company_people_tab.table.status')}</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('company_people_tab.table.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {contacts.map((contact) => (
              <tr key={contact.role_id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3">
                  <div
                    className="flex items-center gap-3 cursor-pointer"
                    onClick={() => onNavigateToContact?.(contact.contact_id)}
                  >
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-sm font-medium">
                      {contact.avatar_url ? (
                        <img src={contact.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        getInitials(contact.full_name)
                      )}
                    </div>
                    <span className="font-medium text-gray-900 hover:text-blue-600">{contact.full_name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {editingRoleId === contact.role_id ? (
                    <input
                      type="text"
                      value={editRoleForm.title || ''}
                      onChange={(e) => setEditRoleForm({ ...editRoleForm, title: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t('company_people_tab.table.title')}
                    />
                  ) : (
                    contact.title || '-'
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {editingRoleId === contact.role_id ? (
                    <input
                      type="text"
                      value={editRoleForm.department || ''}
                      onChange={(e) => setEditRoleForm({ ...editRoleForm, department: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t('company_people_tab.table.department')}
                    />
                  ) : (
                    contact.department || '-'
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {contact.email || '-'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {editingRoleId === contact.role_id ? (
                      <>
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={editRoleForm.is_current ?? contact.is_current}
                            onChange={(e) => setEditRoleForm({ ...editRoleForm, is_current: e.target.checked })}
                          />
                          {t('company_people_tab.status.current')}
                        </label>
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={editRoleForm.is_primary ?? contact.is_primary}
                            onChange={(e) => setEditRoleForm({ ...editRoleForm, is_primary: e.target.checked })}
                          />
                          {t('company_people_tab.status.primary')}
                        </label>
                      </>
                    ) : (
                      <>
                        {contact.is_current && (
                          <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">{t('company_people_tab.status.current')}</span>
                        )}
                        {contact.is_primary && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{t('company_people_tab.status.primary')}</span>
                        )}
                      </>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  {editingRoleId === contact.role_id ? (
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleRoleUpdate(contact.role_id)}
                        className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors"
                        title={t('company_people_tab.actions_labels.save')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                      <button
                        onClick={cancelEditingRole}
                        className="p-1 text-gray-500 hover:bg-gray-100 rounded transition-colors"
                        title={t('company_people_tab.actions_labels.cancel')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditingRole(contact);
                        }}
                        className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title={t('company_people_tab.actions_labels.edit_role')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRoleDelete(contact.role_id, contact.full_name);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title={t('company_people_tab.actions_labels.remove_from_company')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
